import type { Feature, PlanGateError } from '@/types';
import type { RouteLocationNormalized } from 'vue-router';

/**
 * Plan-gating navigation decision (MILO-282), extracted so it can be unit-tested
 * without booting the whole router. Frontend upsells, backend enforces.
 *
 * - A route that declares `meta.feature` the plan doesn't include → open the
 *   `feature_not_in_plan` upgrade modal and block.
 * - A `*-create` / `*-edit` route while the account is read-only (lapsed) → open
 *   the `account_lapsed` modal and block.
 *
 * Blocking returns `false` (stay put, so the modal surfaces in context) unless
 * this is a direct load with no previous route, where we land on home instead.
 */
export type PlanGateContext = {
    allows: (feature: Feature) => boolean;
    isReadOnly: boolean;
    trigger: (error: PlanGateError) => void;
};

export type PlanGateResult = false | { name: string } | undefined;

export function evaluatePlanGate(
    to: Pick<RouteLocationNormalized, 'name' | 'meta'>,
    from: Pick<RouteLocationNormalized, 'name'>,
    ctx: PlanGateContext,
): PlanGateResult {
    const fallback: PlanGateResult = from.name ? false : { name: 'home' };

    const feature = to.meta?.feature as Feature | undefined;
    if (feature && !ctx.allows(feature)) {
        ctx.trigger({ message: '', code: 'feature_not_in_plan', feature, required_plan: 'professional' });
        return fallback;
    }

    const name = typeof to.name === 'string' ? to.name : '';
    if ((name.endsWith('-create') || name.endsWith('-edit')) && ctx.isReadOnly) {
        ctx.trigger({ message: '', code: 'account_lapsed' });
        return fallback;
    }

    return undefined;
}
